import React, { useState, useMemo } from 'react';
import { useApp } from '../contexts/AppContext';
import { fmtDatum, berekenUrenPeriode, berekenVerlofUrenHeleDag } from '../utils/datum';
import sharepointConfig from '../config/sharepointConfig';

const DAGEN = ['ma','di','wo','do','vr'];
const DAG_LABELS = { ma: 'Ma', di: 'Di', wo: 'Wo', do: 'Do', vr: 'Vr' };

function MedewerkerVerlof() {
  const {
    currentUser, medewerkers, verlofaanvragen, overuren, feestdagen, vakantiePerioden,
    verlofTypes, indienVerlofaanvraag,
  } = useApp();

  const currentYear = new Date().getFullYear();

  const [portaalMwId, setPortaalMwId] = useState('');
  const [portaalForm, setPortaalForm] = useState({ Type: '', StartDatum: '', EindDatum: '', Uren: '', Reden: '', isDagdeel: false, StartTijd: '', EindTijd: '', gebruikOveruren: false, directGoedkeuren: true });
  const [portaalSubmitting, setPortaalSubmitting] = useState(false);
  const [portaalSuccess, setPortaalSuccess] = useState('');

  const portaalMw = medewerkers.find(m => m.Id === portaalMwId);
  const portaalSaldo = useMemo(() => {
    if (!portaalMw) return null;
    const berekendeUren = portaalMw.BerekendeVerlofUren || 0;
    const meegenomenUren = portaalMw.MeegenomenUren || 0;
    const totaalUren = berekendeUren + meegenomenUren;
    const opgenomenVerlof = verlofaanvragen
      .filter(v => v.medewerkerId === portaalMwId && v.Status === 'goedgekeurd' && new Date(v.StartDatum).getFullYear() === currentYear)
      .filter(v => { const vt = verlofTypes.find(t => t.key === v.Type); return vt ? (vt.aftrekSaldo === 'verlof' || (vt.trektAfVanVerlof !== false && vt.aftrekSaldo !== 'flex')) : true; })
      .reduce((sum, v) => sum + Math.max(0, (v.Uren || 0) - (v.overurenGebruikt || 0)), 0);
    const overurenVerdiend = overuren.filter(o => o.medewerkerId === portaalMwId && o.Status === 'goedgekeurd').reduce((s, o) => s + (o.uren || o.Uren || 0), 0);
    const overurenGebruikt = verlofaanvragen.filter(v => v.medewerkerId === portaalMwId && v.Status === 'goedgekeurd' && new Date(v.StartDatum).getFullYear() === currentYear).reduce((s, v) => s + (v.overurenGebruikt || 0), 0);
    // Zaterdag onbetaald drempel
    const zaterdagDrempel = portaalMw.ZaterdagDrempel || 0;
    const todayStr = new Date().toISOString().split('T')[0];
    const isZaterdag = (d) => d && new Date(d + 'T12:00:00').getDay() === 6;
    const goedgekeurdeOveruren = overuren.filter(o => o.medewerkerId === portaalMwId && o.Status === 'goedgekeurd' && (o.datum || '') <= todayStr);
    const zaterdagUrenTotaal = goedgekeurdeOveruren.filter(o => isZaterdag(o.datum)).reduce((s, o) => s + (o.uren || o.Uren || 0), 0);
    const doordeweeksUren = goedgekeurdeOveruren.filter(o => !isZaterdag(o.datum)).reduce((s, o) => s + (o.uren || o.Uren || 0), 0);
    const zaterdagOnbetaald = zaterdagDrempel > 0 ? Math.min(zaterdagUrenTotaal, zaterdagDrempel) : 0;
    const zaterdagAlsOveruren = zaterdagDrempel > 0 ? Math.max(0, zaterdagUrenTotaal - zaterdagDrempel) : zaterdagUrenTotaal;
    const overurenSaldoCorrected = (doordeweeksUren + zaterdagAlsOveruren) - overurenGebruikt;
    const flexPlus = verlofaanvragen.filter(v => v.medewerkerId === portaalMwId && v.Status === 'goedgekeurd' && v.Type === 'flex-plus' && new Date(v.StartDatum).getFullYear() === currentYear).reduce((s, v) => s + (v.Uren || 0), 0);
    const flexMin = verlofaanvragen.filter(v => { if (v.medewerkerId !== portaalMwId || v.Status !== 'goedgekeurd' || new Date(v.StartDatum).getFullYear() !== currentYear) return false; const vt = verlofTypes.find(t => t.key === v.Type); return vt && vt.aftrekSaldo === 'flex'; }).reduce((s, v) => s + (v.Uren || 0), 0);
    return { berekendeUren, meegenomenUren, totaalUren, opgenomenUren: opgenomenVerlof, resterend: totaalUren - opgenomenVerlof, overurenSaldo: overurenSaldoCorrected, weekFlexUren: portaalMw.WeekFlexUren || 0, flexBalans: Math.min(10, flexPlus - flexMin), zaterdagDrempel, zaterdagUrenTotaal, zaterdagOnbetaald, zaterdagAlsOveruren };
  }, [portaalMwId, portaalMw, verlofaanvragen, overuren, verlofTypes, currentYear]);
  const portaalVerlof = useMemo(() => portaalMwId ? verlofaanvragen.filter(v => v.medewerkerId === portaalMwId).sort((a, b) => (b.StartDatum || '').localeCompare(a.StartDatum || '')) : [], [portaalMwId, verlofaanvragen]);

  // Check vakantieperiode overlap
  const getVakantieWarning = (startDatum, eindDatum) => {
    if (!startDatum || !vakantiePerioden || vakantiePerioden.length === 0) return null;
    const sd = startDatum;
    const ed = eindDatum || startDatum;
    const overlapping = vakantiePerioden.filter(vp =>
      sd <= vp.eindDatum && ed >= vp.startDatum
    );
    if (overlapping.length === 0) return null;
    return overlapping.map(vp => vp.naam).join(', ');
  };

  const vakantieWarning = getVakantieWarning(portaalForm.StartDatum, portaalForm.EindDatum || portaalForm.StartDatum);

  // Uren berekening
  const uren = parseFloat(String(portaalForm.Uren).replace(',', '.')) || 0;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>👤 Verlofaanvraag medewerkers</h3>
        <p style={{ fontSize: 13, color: '#605e5c', marginBottom: 16 }}>Selecteer een medewerker om hun verlofsaldo te bekijken en verlofaanvragen in te dienen.</p>
        <select style={{ ...styles.editInput, width: '100%', maxWidth: 400, marginBottom: 20 }}
          value={portaalMwId} onChange={e => { setPortaalMwId(e.target.value); setPortaalSuccess(''); setPortaalForm({ Type: '', StartDatum: '', EindDatum: '', Uren: '', Reden: '', isDagdeel: false, StartTijd: '', EindTijd: '', gebruikOveruren: false, directGoedkeuren: true }); }}>
          <option value="" disabled>--- Kies ---</option>
          {medewerkers.filter(m => (m.Status || 'actief') === 'actief').map(m => (
            <option key={m.Id} value={m.Id}>{m.Naam} ({m.Afdeling || 'Medewerker'})</option>
          ))}
        </select>

        {portaalMw && portaalSaldo && (
          <>
            {/* Saldo breakdown */}
            <div style={{ background: '#f0f6ff', border: '1px solid #c7e0f4', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0078D4', marginBottom: 12 }}>🏖️ Verlofsaldo — {portaalMw.Naam}</div>
              <div className="r-label-grid" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 16px', fontSize: 14 }}>
                <span style={{ color: '#605e5c' }}>Standaard verlofuren:</span><strong>{portaalSaldo.berekendeUren}u</strong>
                {portaalSaldo.meegenomenUren > 0 && <><span style={{ color: '#8764B8' }}>📦 Meegenomen vorig jaar:</span><strong style={{ color: '#8764B8' }}>+{portaalSaldo.meegenomenUren}u</strong></>}
                <span style={{ borderTop: '1px solid #c7e0f4', paddingTop: 4, fontWeight: 600 }}>Totaal beschikbaar:</span><span style={{ borderTop: '1px solid #c7e0f4', paddingTop: 4, fontWeight: 700 }}>{portaalSaldo.totaalUren}u</span>
                <span style={{ color: '#605e5c' }}>Opgenomen:</span><span>−{portaalSaldo.opgenomenUren}u</span>
                <span style={{ fontWeight: 700, color: portaalSaldo.resterend >= 0 ? '#107C10' : '#D13438' }}>Resterend:</span>
                <span style={{ fontWeight: 700, color: portaalSaldo.resterend >= 0 ? '#107C10' : '#D13438' }}>{portaalSaldo.resterend}u</span>
                {portaalSaldo.overurenSaldo > 0 && <><span style={{ color: '#E86E1C' }}>⏰ Overuren saldo:</span><strong style={{ color: '#E86E1C' }}>{portaalSaldo.overurenSaldo}u</strong></>}
                {(portaalSaldo.zaterdagDrempel || 0) > 0 && (
                  <>
                    <span style={{ color: '#8B6914', borderTop: '1px solid #c7e0f4', paddingTop: 4 }}>📅 Zaterdaguren (onbetaald):</span>
                    <span style={{ borderTop: '1px solid #c7e0f4', paddingTop: 4 }}>
                      <strong style={{ color: portaalSaldo.zaterdagUrenTotaal >= portaalSaldo.zaterdagDrempel ? '#107C10' : '#8B6914' }}>
                        {portaalSaldo.zaterdagUrenTotaal || 0} / {portaalSaldo.zaterdagDrempel}u
                      </strong>
                    </span>
                    <span style={{ fontSize: 12, color: '#605e5c' }}>
                      {portaalSaldo.zaterdagUrenTotaal >= portaalSaldo.zaterdagDrempel
                        ? `✅ Drempel bereikt · ${portaalSaldo.zaterdagAlsOveruren || 0}u telt als overuren`
                        : `Nog ${portaalSaldo.zaterdagDrempel - (portaalSaldo.zaterdagUrenTotaal || 0)}u onbetaald`}
                    </span>
                    <span></span>
                  </>
                )}
              </div>
            </div>

            {portaalSuccess && <div style={{ background: '#dff6dd', border: '1px solid #a7e3a5', borderRadius: 10, padding: '10px 16px', marginBottom: 16, color: '#107C10', fontSize: 13 }}>✅ {portaalSuccess}</div>}

            {/* Verlof indienen formulier */}
            <div style={{ background: '#faf9f8', border: '1px solid #edebe9', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
              <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: '#1a1a2e' }}>Verlof indienen voor {portaalMw.Naam.split(' ')[0]}</h4>
              <div className="r-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={styles.editLabel}>Type verlof</label>
                  <select style={styles.editInput} value={portaalForm.Type} onChange={e => setPortaalForm({...portaalForm, Type: e.target.value})}>
                    <option value="" disabled>--- Kies ---</option>
                    {verlofTypes.filter(t => t.key !== 'ziek').map(t => <option key={t.key} value={t.key}>{t.text}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                        <input type="checkbox" checked={portaalForm.isDagdeel} onChange={e => {
                          const isDagdeel = e.target.checked;
                          setPortaalForm(f => ({...f, isDagdeel, StartTijd: isDagdeel ? f.StartTijd : '', EindTijd: isDagdeel ? f.EindTijd : '', Uren: isDagdeel ? '' : f.Uren}));
                        }} />
                        🕐 Met start- en eindtijd (automatische urenberekening)
                      </label>
                    </div>
                    {portaalForm.isDagdeel ? (
                      <>
                        <div>
                          <label style={styles.editLabel}>Startdatum</label>
                          <input type="date" style={styles.editInput} value={portaalForm.StartDatum} onChange={e => {
                            const sd = e.target.value;
                            const ed = portaalForm.EindDatum || sd;
                            const info = berekenUrenPeriode(sd, portaalForm.StartTijd, ed, portaalForm.EindTijd, feestdagen.map(f=>f.Datum), portaalMw?.Werkrooster);
                            setPortaalForm(f => ({...f, StartDatum: sd, Uren: info.netto > 0 ? String(info.netto) : f.Uren}));
                          }} />
                        </div>
                        <div>
                          <label style={styles.editLabel}>Starttijd</label>
                          <input type="time" style={styles.editInput} value={portaalForm.StartTijd} onChange={e => {
                            const st = e.target.value;
                            const ed = portaalForm.EindDatum || portaalForm.StartDatum;
                            const info = berekenUrenPeriode(portaalForm.StartDatum, st, ed, portaalForm.EindTijd, feestdagen.map(f=>f.Datum), portaalMw?.Werkrooster);
                            setPortaalForm(f => ({...f, StartTijd: st, Uren: info.netto > 0 ? String(info.netto) : ''}));
                          }} />
                        </div>
                        <div>
                          <label style={styles.editLabel}>Einddatum</label>
                          <input type="date" style={styles.editInput} value={portaalForm.EindDatum || portaalForm.StartDatum} onChange={e => {
                            const ed = e.target.value;
                            const info = berekenUrenPeriode(portaalForm.StartDatum, portaalForm.StartTijd, ed, portaalForm.EindTijd, feestdagen.map(f=>f.Datum), portaalMw?.Werkrooster);
                            setPortaalForm(f => ({...f, EindDatum: ed, Uren: info.netto > 0 ? String(info.netto) : f.Uren}));
                          }} />
                        </div>
                        <div>
                          <label style={styles.editLabel}>Eindtijd</label>
                          <input type="time" style={styles.editInput} value={portaalForm.EindTijd} onChange={e => {
                            const et = e.target.value;
                            const ed = portaalForm.EindDatum || portaalForm.StartDatum;
                            const info = berekenUrenPeriode(portaalForm.StartDatum, portaalForm.StartTijd, ed, et, feestdagen.map(f=>f.Datum), portaalMw?.Werkrooster);
                            setPortaalForm(f => ({...f, EindTijd: et, Uren: info.netto > 0 ? String(info.netto) : ''}));
                          }} />
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                          <label style={styles.editLabel}>Uren berekening</label>
                          <input type="text" inputMode="decimal" style={styles.editInput} value={portaalForm.Uren} onChange={e => setPortaalForm({...portaalForm, Uren: e.target.value})} placeholder="bv. 8" />
                        </div>
                        {portaalForm.StartDatum && portaalForm.StartTijd && portaalForm.EindTijd && (() => {
                          const ed = portaalForm.EindDatum || portaalForm.StartDatum;
                          const info = berekenUrenPeriode(portaalForm.StartDatum, portaalForm.StartTijd, ed, portaalForm.EindTijd, feestdagen.map(f=>f.Datum), portaalMw?.Werkrooster);
                          if (info.netto <= 0) return null;
                          return (
                            <div style={{ gridColumn: 'span 2', fontSize: 12, color: '#605e5c', background: '#f0f6ff', padding: '8px 12px', borderRadius: 8, lineHeight: 1.6 }}>
                              ⏱️ {fmtDatum(portaalForm.StartDatum)} {portaalForm.StartTijd} → {fmtDatum(ed)} {portaalForm.EindTijd} = <strong>{info.netto}u</strong>
                              {info.werkdagen > 1 && ` (${info.werkdagen} werkdagen)`}
                              {info.details.length > 1 && info.details.filter(d=>d.uren>0).map((d,i) => (
                                <div key={i} style={{ fontSize: 11, color: '#8a8886' }}>
                                  {fmtDatum(d.datum)}: {d.type === 'hele dag' ? `${d.uren}u` : `${d.van}–${d.tot} = ${d.uren}u`}{d.pauzeTrekt ? ' (−pauze)' : ''}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </>
                    ) : (
                      <>
                        <div>
                          <label style={styles.editLabel}>Startdatum</label>
                          <input type="date" style={styles.editInput} value={portaalForm.StartDatum} onChange={e => {
                            const sd = e.target.value;
                            if (portaalForm.EindDatum && portaalMw) {
                              const hd = berekenVerlofUrenHeleDag(sd, portaalForm.EindDatum, feestdagen.map(f=>f.Datum), portaalMw?.Werkrooster);
                              setPortaalForm(f => ({...f, StartDatum: sd, Uren: hd.uren > 0 ? String(hd.uren) : f.Uren}));
                            } else { setPortaalForm(f => ({...f, StartDatum: sd})); }
                          }} />
                        </div>
                        <div>
                          <label style={styles.editLabel}>Einddatum</label>
                          <input type="date" style={styles.editInput} value={portaalForm.EindDatum} onChange={e => {
                            const ed = e.target.value;
                            if (portaalForm.StartDatum && portaalMw) {
                              const hd = berekenVerlofUrenHeleDag(portaalForm.StartDatum, ed, feestdagen.map(f=>f.Datum), portaalMw?.Werkrooster);
                              setPortaalForm(f => ({...f, EindDatum: ed, Uren: hd.uren > 0 ? String(hd.uren) : f.Uren}));
                            } else { setPortaalForm(f => ({...f, EindDatum: ed})); }
                          }} />
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                          <label style={styles.editLabel}>Uren berekening</label>
                          <input type="text" inputMode="decimal" style={styles.editInput} value={portaalForm.Uren} onChange={e => setPortaalForm({...portaalForm, Uren: e.target.value})} placeholder="bv. 8" />
                          {portaalForm.StartDatum && portaalForm.EindDatum && portaalMw && (() => {
                            const hd = berekenVerlofUrenHeleDag(portaalForm.StartDatum, portaalForm.EindDatum, feestdagen.map(f=>f.Datum), portaalMw?.Werkrooster);
                            return hd.werkdagen > 0 ? <div style={{ fontSize: 10, color: '#605e5c', marginTop: 2 }}>Rooster: {hd.werkdagen} werkdag{hd.werkdagen > 1 ? 'en' : ''} = {hd.uren}u</div> : null;
                          })()}
                        </div>
                      </>
                    )}

                {/* Vakantieperiode waarschuwing */}
                {vakantieWarning && (
                  <div style={{ gridColumn: 'span 2', background: '#FFF4CE', border: '1px solid #FFB900', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#7A6400' }}>
                    ⚠️ <strong>Let op:</strong> Deze verlofaanvraag valt (deels) in een vakantieperiode ({vakantieWarning}). Vanwege bezetting en eventuele meerdere verlofaanvragen van collega's kan goedkeuring niet altijd gegarandeerd worden.
                  </div>
                )}

                {/* Overuren verrekening */}
                {portaalSaldo.overurenSaldo > 0 && (() => {
                  const vt = verlofTypes.find(t => t.key === portaalForm.Type);
                  if (vt && (vt.aftrekSaldo === 'flex' || vt.aftrekSaldo === 'geen')) return null;
                  return (
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                        <input type="checkbox" checked={portaalForm.gebruikOveruren} onChange={e => setPortaalForm({...portaalForm, gebruikOveruren: e.target.checked})} />
                        Overuren inzetten ({portaalSaldo.overurenSaldo}u beschikbaar)
                      </label>
                      {portaalForm.gebruikOveruren && Number(portaalForm.Uren) > 0 && (() => {
                        const ov = Math.min(portaalSaldo.overurenSaldo, Number(portaalForm.Uren));
                        return <div style={{ fontSize: 12, color: '#605e5c', marginLeft: 28, marginTop: 4 }}>⏰ {ov}u overuren · 🏖️ {Math.max(0, Number(portaalForm.Uren) - ov)}u verlof</div>;
                      })()}
                    </div>
                  );
                })()}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={styles.editLabel}>Opmerkingen (optioneel)</label>
                  <input style={styles.editInput} value={portaalForm.Reden} onChange={e => setPortaalForm({...portaalForm, Reden: e.target.value})} placeholder="Bijv. familiebezoek..." />
                </div>
              </div>
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: portaalForm.directGoedkeuren ? '#107C10' : '#605e5c' }}>
                  <input type="checkbox" checked={portaalForm.directGoedkeuren} onChange={e => setPortaalForm({...portaalForm, directGoedkeuren: e.target.checked})} />
                  <span style={{ fontWeight: 600 }}>✅ Direct goedkeuren</span>
                </label>
                <div style={{ fontSize: 11, color: '#605e5c', marginLeft: 28, marginTop: -6 }}>
                  {portaalForm.directGoedkeuren ? 'Status wordt direct "goedgekeurd".' : 'Status wordt "ingediend" — moet nog worden goedgekeurd.'}
                </div>
                <button style={{ ...styles.submitBtn, opacity: portaalSubmitting ? 0.6 : 1, ...(portaalForm.directGoedkeuren ? { background: '#107C10' } : {}) }}
                  disabled={portaalSubmitting || !portaalForm.StartDatum || !portaalForm.Uren}
                  onClick={async () => {
                    setPortaalSubmitting(true);
                    try {
                      const vt = verlofTypes.find(t => t.key === portaalForm.Type);
                      const canUseOv = portaalForm.gebruikOveruren && portaalSaldo.overurenSaldo > 0;
                      const ovGebruikt = canUseOv ? Math.min(portaalSaldo.overurenSaldo, uren) : 0;
                      await indienVerlofaanvraag({
                        medewerkerId: portaalMwId, medewerkerNaam: portaalMw.Naam,
                        ingediendDoor: currentUser.Id, ingediendDoorNaam: currentUser.Naam,
                        Type: portaalForm.Type, StartDatum: portaalForm.StartDatum,
                        EindDatum: portaalForm.EindDatum || portaalForm.StartDatum,
                        StartTijd: portaalForm.isDagdeel ? portaalForm.StartTijd : null,
                        EindTijd: portaalForm.isDagdeel ? portaalForm.EindTijd : null,
                        Uren: uren,
                        Reden: portaalForm.Reden,
                        gebruikOveruren: canUseOv, overurenGebruikt: ovGebruikt,
                        directGoedkeuren: portaalForm.directGoedkeuren,
                      });
                      setPortaalSuccess(`${vt?.text || portaalForm.Type} voor ${portaalMw.Naam} ${portaalForm.directGoedkeuren ? 'goedgekeurd' : 'ingediend'}`);
                      setPortaalForm({ Type: '', StartDatum: '', EindDatum: '', Uren: '', Reden: '', isDagdeel: false, StartTijd: '', EindTijd: '', gebruikOveruren: false, directGoedkeuren: true });
                    } catch (err) { console.error(err); } finally { setPortaalSubmitting(false); }
                  }}>{portaalSubmitting ? 'Bezig...' : (portaalForm.directGoedkeuren ? `✅ Goedkeuren voor ${portaalMw?.Naam?.split(' ')[0] || '...'}` : 'Aanvraag indienen')}</button>
              </div>
            </div>

            {/* Verlofoverzicht medewerker */}
            <div style={styles.card}>
              <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>📋 Verlofaanvragen ({portaalVerlof.length})</h4>
              {portaalVerlof.length === 0 ? <p style={{ color: '#a19f9d', fontSize: 13 }}>Geen aanvragen gevonden.</p> : (
                <table style={styles.table} className="r-table">
                  <thead><tr>
                    <th style={styles.th}>Type verlof</th><th style={styles.th}>Periode</th><th style={styles.th}>Uren</th>
                    <th style={styles.th}>Status</th><th style={styles.th}>Ingediend door</th><th style={styles.th}>Opmerkingen</th>
                  </tr></thead>
                  <tbody>
                    {portaalVerlof.slice(0, 25).map(v => {
                      const vt = verlofTypes.find(t => t.key === v.Type);
                      const si = sharepointConfig.statusOpties.find(s => s.key === v.Status);
                      const ingediendLabel = v.ingediendDoor === v.medewerkerId ? 'Zelf'
                        : (medewerkers.find(m => m.Id === v.ingediendDoor)?.IsAdmin ? `👑 ${v.IngediendDoorNaam || 'Beheerder'}`
                          : `📋 ${v.IngediendDoorNaam || 'Manager'}`);
                      return (
                        <tr key={v.Id} style={styles.tr}>
                          <td style={styles.td}>{vt?.text || v.Type}</td>
                          <td style={styles.td}>{fmtDatum(v.StartDatum)}{v.EindDatum && v.EindDatum !== v.StartDatum ? ` – ${fmtDatum(v.EindDatum)}` : ''}</td>
                          <td style={styles.td}>{v.Uren || 0}u</td>
                          <td style={styles.td}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: `${si?.color || '#767676'}20`, color: si?.color || '#767676' }}>{si?.icon} {si?.text || v.Status}</span></td>
                          <td style={styles.td}><span style={{ fontSize: 12, color: '#605e5c' }}>{ingediendLabel}</span></td>
                          <td style={styles.td}><span style={{ fontSize: 12, color: '#a19f9d' }}>{v.Reden || '-'}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Werkrooster preview */}
            {portaalMw.Werkrooster && (
              <div style={{ ...styles.card, marginTop: 16 }}>
                <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>🕐 Werkrooster</h4>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {DAGEN.map(d => {
                    const dag = portaalMw.Werkrooster[d];
                    if (!dag || (!dag.ochtend && !dag.middag && !dag.avond)) return <div key={d} style={{ padding: '6px 12px', background: '#f3f2f1', borderRadius: 8, fontSize: 12, color: '#a19f9d' }}>{DAG_LABELS[d]}: vrij</div>;
                    return <div key={d} style={{ padding: '6px 12px', background: '#dff6dd', borderRadius: 8, fontSize: 12, color: '#107C10' }}>{DAG_LABELS[d]}: {[dag.ochtend, dag.middag, dag.avond].filter(Boolean).join(' · ')}</div>;
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  card: { background: '#fff', border: '1px solid #edebe9', borderRadius: 14, padding: '24px 28px', marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  cardTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 },
  editLabel: { display: 'block', fontSize: 13, fontWeight: 600, color: '#323130', marginBottom: 6 },
  editInput: { width: '100%', padding: '8px 12px', border: '1px solid #d2d0ce', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  submitBtn: { padding: '10px 24px', background: '#E86E1C', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #edebe9', color: '#605e5c', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' },
  td: { padding: '10px 12px', borderBottom: '1px solid #f3f2f1', verticalAlign: 'middle' },
  tr: { transition: 'background 0.15s' },
};

export default MedewerkerVerlof;
